import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, Save, Globe, Phone, Mail, MapPin, MessageCircle, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminSidebar from '../../components/AdminSidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_name: 'Indicraft',
    tagline: 'Sacred Puja Essentials',
    logo_url: '',
    favicon_url: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    whatsapp_number: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    youtube_url: '',
    google_analytics_id: '',
    facebook_pixel_id: ''
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/site-settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/site-settings`, settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-maroon"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminSidebar active="settings" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <header className="bg-white border-b border-gold/10 px-4 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-maroon">Site Settings</h1>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-maroon hover:bg-maroon-dark text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </header>

        <main className="p-4 md:p-6 space-y-6">
          {/* Brand Settings */}
          <div className="bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="text-lg font-heading font-semibold text-maroon mb-6 flex items-center gap-2">
              <Globe className="w-5 h-5 text-gold" />
              Brand Settings
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Site Name</label>
                <Input
                  value={settings.site_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, site_name: e.target.value }))}
                  placeholder="Indicraft"
                  className="border-maroon/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Tagline</label>
                <Input
                  value={settings.tagline}
                  onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="Sacred Puja Essentials"
                  className="border-maroon/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Logo URL</label>
                <Input
                  value={settings.logo_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="https://..."
                  className="border-maroon/20"
                />
                {settings.logo_url && (
                  <img src={settings.logo_url} alt="Logo Preview" className="mt-2 h-12 object-contain" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Favicon URL</label>
                <Input
                  value={settings.favicon_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, favicon_url: e.target.value }))}
                  placeholder="https://..."
                  className="border-maroon/20"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="text-lg font-heading font-semibold text-maroon mb-6 flex items-center gap-2">
              <Phone className="w-5 h-5 text-gold" />
              Contact Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <Input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="support@indicraft.in"
                  className="border-maroon/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone
                </label>
                <Input
                  value={settings.contact_phone}
                  onChange={(e) => setSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+91 123-456-7890"
                  className="border-maroon/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">
                  <MessageCircle className="w-4 h-4 inline mr-1" />
                  WhatsApp Number
                </label>
                <Input
                  value={settings.whatsapp_number}
                  onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                  placeholder="+91 9876543210"
                  className="border-maroon/20"
                />
                <p className="text-xs text-maroon/50 mt-1">Include country code. This enables the floating WhatsApp button.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Address
                </label>
                <Input
                  value={settings.address}
                  onChange={(e) => setSettings(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Mumbai, Maharashtra, India"
                  className="border-maroon/20"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="text-lg font-heading font-semibold text-maroon mb-6">
              Social Media Links
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Facebook URL</label>
                <Input
                  value={settings.facebook_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, facebook_url: e.target.value }))}
                  placeholder="https://facebook.com/..."
                  className="border-maroon/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Instagram URL</label>
                <Input
                  value={settings.instagram_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, instagram_url: e.target.value }))}
                  placeholder="https://instagram.com/..."
                  className="border-maroon/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Twitter URL</label>
                <Input
                  value={settings.twitter_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, twitter_url: e.target.value }))}
                  placeholder="https://twitter.com/..."
                  className="border-maroon/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">YouTube URL</label>
                <Input
                  value={settings.youtube_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, youtube_url: e.target.value }))}
                  placeholder="https://youtube.com/..."
                  className="border-maroon/20"
                />
              </div>
            </div>
          </div>

          {/* Analytics */}
          <div className="bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="text-lg font-heading font-semibold text-maroon mb-6">
              Analytics & Tracking
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Google Analytics ID</label>
                <Input
                  value={settings.google_analytics_id}
                  onChange={(e) => setSettings(prev => ({ ...prev, google_analytics_id: e.target.value }))}
                  placeholder="G-XXXXXXXXXX"
                  className="border-maroon/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-maroon/70 mb-1">Facebook Pixel ID</label>
                <Input
                  value={settings.facebook_pixel_id}
                  onChange={(e) => setSettings(prev => ({ ...prev, facebook_pixel_id: e.target.value }))}
                  placeholder="1234567890"
                  className="border-maroon/20"
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminSettings;
